import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core'
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { Subject, takeUntil } from 'rxjs'
import { EmailComponentType } from '../../../../interfaces/email-component.interface'
import { TemplateComponent } from '../../../../interfaces/email-template.interface'
import { EmailTheme } from '../../../../interfaces/email-theme.interface'

@Component({
  selector: 'ngx-component-properties',
  templateUrl: './component-properties.component.html',
  styleUrls: ['./component-properties.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule]
})
export class ComponentPropertiesComponent implements OnChanges {
  @Input() component: TemplateComponent | null = null
  @Input() theme: EmailTheme | null = null
  @Input() availableComponents: EmailComponentType[] = []
  @Output() propertiesChange = new EventEmitter<Record<string, any>>()

  propertiesForm: FormGroup = new FormGroup({})
  componentType: EmailComponentType | null = null
  private destroy$ = new Subject<void>()

  constructor(private fb: FormBuilder) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['component'] && this.component) {
      this.loadComponentProperties()
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  private loadComponentProperties(): void {
    if (!this.component) return

    // Find the component type definition
    this.componentType = this.availableComponents.find((ct) => ct.slug === this.component!.slug) || null

    if (!this.componentType) return

    // Build form based on component variables
    const formControls: Record<string, any> = {}

    this.componentType.variables?.forEach((variable) => {
      // Use variable_values as the primary source, fallback to default_value
      const currentValue = this.component!.variable_values?.[variable.name] || variable.default_value || ''

      // Use name as the form control key - much simpler!
      formControls[variable.name] = [currentValue]
    })

    this.propertiesForm = this.fb.group(formControls)

    // Listen for form changes
    this.propertiesForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.propertiesChange.emit(value)
    })
  }

  getVariableInputType(variableType: string): string {
    switch (variableType) {
      case 'url':
        return 'url'
      case 'number':
        return 'number'
      case 'color':
        return 'color'
      default:
        return 'text'
    }
  }

  isTextArea(variableType: string): boolean {
    return variableType === 'text' || variableType === 'html'
  }

  isColorPicker(variableType: string): boolean {
    return variableType === 'color'
  }

  getVariableValue(variableName: string): any {
    return this.propertiesForm.get(variableName)?.value || ''
  }

  formatValidationRule(rule: string): string {
    // Format validation rules for display
    switch (rule) {
      case 'required':
        return 'Required'
      case 'string':
        return 'Text'
      case 'url':
        return 'Valid URL'
      default:
        if (rule.startsWith('max:')) {
          return `Max ${rule.split(':')[1]} characters`
        }
        if (rule.startsWith('min:')) {
          return `Min ${rule.split(':')[1]} characters`
        }
        return rule
    }
  }
}
